import type { ClientLegalText } from '@DigitaleDoerfer/digitale-doerfer-api';
import { LinearGradient } from 'expo-linear-gradient';
import { useCallback, useMemo, type FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Button, Text } from 'react-native-paper';
import type { EdgeInsets } from 'react-native-safe-area-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useAcceptLegalTexts } from '@/features/api/hooks/shared-legal/useAcceptLegalTexts';
import { useGetCurrentLegalTexts } from '@/features/api/hooks/shared-legal/useGetCurrentLegalTexts';
import { ListSection } from '@/features/list/components/ListSection';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationListItem } from '@/navigation/components/NavigationListItem';
import { AppRoute } from '@/navigation/types/navigation';
import { StateView } from '@/shared/components/state-view/StateView';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';
import { EditMode } from '@/types';

const makeStyles = (theme: AppTheme, insets: EdgeInsets) =>
  StyleSheet.create({
    gradient: { flex: 1 },
    wrapper: { flex: 1 },
    contentWrapper: { gap: 32, paddingBottom: 32 + insets.bottom },
    contentWrapperSticky: { gap: 32, paddingBottom: 32 },
    footer: {
      paddingHorizontal: 16,
      paddingTop: 16,
      paddingBottom: 16 + insets.bottom,
      backgroundColor: theme.colors.surfaceContainerLow
    },
    header: { alignItems: 'center', gap: 12 },
    description: { textAlign: 'center' },
    age: { fontWeight: 'bold' },
    list: {
      marginVertical: 0,
      backgroundColor: theme.colors.surfaceContainerLow
    }
  });

type Props = {
  mode?: EditMode;
  /**
   * Renders the accept button in a fixed footer below the scrollable content
   * instead of as the last scrollable element. Opt-in for flows (e.g. the
   * registration wizard) where long content would push the button out of view.
   * Has no effect in READ_ONLY mode (no button is rendered).
   */
  stickyButton?: boolean;
  onAccept?: (
    legalTextIdentifier?: ClientLegalText['legalTextIdentifier'][]
  ) => void;
};

export const Terms: FC<Props> = ({
  mode = EditMode.READ_ONLY,
  stickyButton = false,
  onAccept
}) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme, insets), [insets, theme]);

  const { t } = useTranslation(LocalizationNameSpace.PRIVACY, {
    keyPrefix: 'components.terms'
  });

  const isReadOnly = mode === EditMode.READ_ONLY;

  const { data: legalTexts, isPending, isError } = useGetCurrentLegalTexts();
  const { mutate: accept, isPending: isAcceptLoading } = useAcceptLegalTexts();

  const handleAccept = useCallback(() => {
    const legalTextIdentifier = legalTexts
      ?.filter(({ required }) => required)
      ?.map(({ legalTextIdentifier }) => legalTextIdentifier)
      .filter((identifier) => !isEmptyOrNull(identifier));

    // in case the view is used during the registration, the accept is handled in registration screen
    if (mode === EditMode.REGISTRATION) {
      onAccept?.(legalTextIdentifier);
      return;
    }

    accept(
      { legalTextIdentifiersToAccept: legalTextIdentifier },
      {
        onSuccess: () => {
          onAccept?.(legalTextIdentifier);
        }
      }
    );
  }, [accept, legalTexts, mode, onAccept]);

  const content = (
    <>
      {!isReadOnly && (
        <View style={styles.header}>
          <Text variant="headlineLarge">{t('title')}</Text>
          <Text variant="bodyLarge" style={styles.description}>
            <Trans
              t={t}
              i18nKey="description"
              components={[
                <Text key="age" style={styles.age}>
                  {''}
                </Text>
              ]}
            />
          </Text>
        </View>
      )}
      <StateView isLoading={isPending} isError={isError}>
        <ListSection style={styles.list}>
          {legalTexts?.map(({ name, id }) => (
            <NavigationListItem
              key={id}
              screen={AppRoute.LegalText}
              params={{ name, id }}
              title={name}
            />
          ))}
        </ListSection>
      </StateView>
    </>
  );

  const acceptButton = !isReadOnly && (
    <Button mode="contained" onPress={handleAccept} loading={isAcceptLoading}>
      {t('accept')}
    </Button>
  );

  const scrollBackground = isReadOnly
    ? 'transparent'
    : theme.colors.surfaceContainerLow;

  return (
    <LinearGradient
      style={styles.gradient}
      colors={[
        theme.colors.surfaceContainer,
        theme.colors.secondaryContainer,
        theme.colors.secondaryContainer
      ]}
    >
      {stickyButton && !isReadOnly ? (
        <View style={styles.wrapper}>
          <StateView
            viewStyle={styles.wrapper}
            contentContainerStyle={{
              ...styles.contentWrapperSticky,
              paddingHorizontal: 16
            }}
            backgroundColor={scrollBackground}
          >
            {content}
          </StateView>
          <View style={styles.footer}>{acceptButton}</View>
        </View>
      ) : (
        <StateView
          viewStyle={styles.wrapper}
          contentContainerStyle={{
            ...styles.contentWrapper,
            paddingHorizontal: isReadOnly ? 0 : 16
          }}
          backgroundColor={scrollBackground}
        >
          {content}
          {acceptButton}
        </StateView>
      )}
    </LinearGradient>
  );
};
